import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../BaseComponents/Typography/EDTypography';
import { useSelectedDetailQuery } from '../../../../../Components/QueryComponents/SelectedDetailQuery/useSelectedDetailQuery';
import clientAvenyModel from '../../../../../Model/clientAvenyModel';
import { IClientBusinessModel } from '../../../../../Types/ClientModelTypes';
import Attribut from '../../../_Common/Components/BusinessCollectionComponents/Attribut/Attribut';
import BusinessDataForm, {
  BusinessDataFormContext,
  IBusinessDataFormContext,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  defaultEditMenuCancelButton,
  defaultEditMenuSaveButton,
  IEditMenuAction,
  IFieldValidation,
  IFieldValidations,
  IFormData,
  IValidateFn,
  removeIdsFromNewAssociations,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { ObjektLank } from '../../../_Common/Components/BusinessCollectionComponents/ObjektLank/ObjektLank';
import { ObjektVyKort } from '../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { BisattningsSettingsQuery } from '../../Queries/_QueryTypes/BisattningsSettingsQuery';
import {
  BisattningQuery as IBisattningQuery,
  BisattningQuery_node_TEDBOBisattning as IBisattningNode,
  BisattningQueryVariables as IBisattningVariables,
  BisattningQuery_node_TEDBOBisattning__Associations as IBisattningAssociations,
} from '../../ObjektVyer/AktuellaAvlidna/Queries/_QueryTypes/BisattningQuery';
import BisattningQuery from '../../ObjektVyer/AktuellaAvlidna/Queries/BisattningQuery.gql';
import Etikett from '../../Rapporter/BJEtiketter/BJEtikett';
import { useMutation, useQuery } from '@apollo/client';
import AddMutation from '../../Mutation/BisattningAddMutation.gql';
import UpdateMutation from '../../Mutation/BisattningUpdateMutation.gql';
import {
  BisattningAddMutation as IAddMutation,
  BisattningAddMutationVariables as IAddMutationVariables,
} from '../../Mutation/_QueryTypes/BisattningAddMutation';
import {
  TEDBOBisattningKontaktTypEnum,
  TEDBOBisattningKREMERINGEnum,
  TEDBOBisattningshandelseTypEnum,
  TEDInputBOBisattningAddValues,
  TEDInputBOBisattningUpdateValues,
  TEDInputOPOrgPersLokAddAssociationValues,
  TEDOPOrgPersLokTypEnum,
} from '../../../../../globalQueryTypes';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { useCollectionMutation } from '../../../../../Components/QueryComponents/CollectionMutation/useCollectionMutation';
import {
  BisattningUpdateMutation as IUpdateMutation,
  BisattningUpdateMutationVariables as IUpdateMutationVariables,
} from '../../Mutation/_QueryTypes/BisattningUpdateMutation';
import { IBusinessCollectionProps } from '../../../../../Types/ObjektVy';
import EDDateAndTime from '../../../../../Components/DateAndTime/EDDateAndTime';
import {
  MeQuery,
  MeQueryVariables,
} from '../../../Bokning/Rutiner/Kalender/Queries/MeQuery/_QueryTypes/MeQuery';
import meQuery from '../../../Bokning/Rutiner/Kalender/Queries/MeQuery/MeQuery.gql';
import { LoginStateContext } from '../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import EDOPAvlidenSearchButtons from '../../Components/EDOPAvlidenSearchButtons';
import { tryDateTimeToDateTimeString } from '../../../../../Utils/dateUtils';
import {
  IParseResult,
  ParseResult,
} from '../../../../../Utils/Formatters/FormatterTypes';
import {
  BegravningsbyraQuery,
  BegravningsbyraQueryVariables,
} from '../../Queries/_QueryTypes/BegravningsbyraQuery';
import begravningsbyraQuery from '../../Queries/BegravningsbyraQuery.gql';
import EDIconButton from '../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDAvenyProgress from '../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import placeringLogo from '../../../../../Assets/Branding/Placering.svg';
import ObjektVy from '../../../_Common/Components/BusinessCollectionComponents/ObjektVy/ObjektVy';
import ObjektVySida from '../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import EDDialog from '../../../../../BaseComponents/Dialog/EDDialog';
import { useIsSmallScreen } from '../../../../../Hooks/useFullscreenView';
import QrCodeHandler from './QrCodeHandler';
import { EDTextEdit } from '../../../../../BaseComponents/TextEdit/EDTextEdit';
import ValjPlacering from '../ValjPlacering';
import { TEDParsePersNr } from '../../../../../Utils/Formatters/Parsers/TEDParsePersNr';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface LamnaInProps extends IBusinessCollectionProps {
  aktivBisattningstyp: string;
  bisattningsTypID: string;
  bisattningSettings: BisattningsSettingsQuery | undefined;
  open: boolean;
  lokalInfo: {
    index: number | undefined;
    id: string;
  };
  setOpen: (open: boolean) => void;
}

const bisattningModel: IClientBusinessModel =
  clientAvenyModel.BO.BusinessModels.BOBisattning;

const bisattningKontakterModel: IClientBusinessModel =
  clientAvenyModel.BO.BusinessModels.BOBisattningKontakt;

export const checkPersNr = (
  persNr: string | null | undefined
): IFieldValidation | undefined => {
  let parsePersNr: IParseResult = {
    value: null,
    result: ParseResult.UNDEFINED,
  };
  if (persNr) parsePersNr = TEDParsePersNr.getInstance().parse(persNr);

  if (!persNr || persNr.length < 12 || parsePersNr.result !== ParseResult.OK) {
    return {
      fieldName: 'Avlidna.Person.PERS_NR',
      message:
        !persNr || persNr.length < 12
          ? 'Kräver ett fullständigt personnummer'
          : 'Felaktigt personnummer',
      type: 'RequiredValueHint',
    };
  }
  return undefined;
};

const LamnaIn: React.FC<LamnaInProps> = props => {
  const {
    nodeData,
    loading: nodeLoading,
    refetch: nodeRefetch,
  } = useSelectedDetailQuery<
    IBisattningQuery,
    IBisattningNode,
    IBisattningVariables
  >(BisattningQuery, props.id);

  const [isOpenPDF, setIsOpenPDF] = useState(false);
  const [isOpenPlacering, setOpenPlacering] = useState(false);
  const [persNr, setPersNr] = useState<string>('');
  const [bisattningID, setBisattningID] = useState<string | undefined>(
    undefined
  );
  const [bisattningVersion, setBisattningVersion] = useState<
    number | undefined
  >(undefined);
  const [avlidenID, setAvlidenID] = useState<string | undefined>(undefined);
  const [avlidenVersion, setAvlidenVersion] = useState<number | undefined>(
    undefined
  );
  const [avlidenPersonID, setAvlidenPersonID] = useState<string | undefined>(
    undefined
  );
  const [avlidenPersonVersion, setAvlidenPersonVersion] = useState<
    number | undefined
  >(undefined);
  const [dodsDatum, setDodsDatum] = useState<string | undefined>(undefined);
  const [avlidenPersonAdressID, setAvlidenPersonAdressID] = useState<
    string | undefined
  >(undefined);
  const [avlidenPersonAdressVersion, setAvlidenPersonAdressVersion] = useState<
    number | undefined
  >(undefined);
  const [begravningsbyraID, setBegravningsbyraID] = useState<
    string | undefined
  >(undefined);
  const [bisattningBegrbyraID, setBisattningBegravningsbyraID] = useState<
    string | undefined
  >(undefined);
  const [bisattningTransportorID, setBisattningTransportorID] = useState<
    string | undefined
  >(undefined);
  const [senastBisattningHandelseTyp, setSenastBisattningHandelseTyp] =
    useState<string | undefined>(undefined);
  const [fetchedBegrDatTid, setFetchedBegrDatTid] = useState('');
  const [fetchedGravsDatTid, setFetchedGravsDatTid] = useState('');
  const [QRText, setQRText] = useState('');
  const [addedBegrTid, setAddedBegrTid] = useState('');
  const [addedBegrDat, setAddedBegrDat] = useState('');
  const [addedGravsTid, setAddedGravsTid] = useState('');
  const [addedGravsDat, setAddedGravsDat] = useState('');
  const [notering, setNotering] = useState<string | undefined>(undefined);
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const loginStateContext = useContext(LoginStateContext);
  const isSmallScreen = useIsSmallScreen();
  const [bisattningsTypID, setBisattningsTypID] = useState<string | undefined>(
    props.bisattningsTypID
  );
  const [bisattningsTyp, setBisattningsTyp] = useState<string | undefined>('');
  const [aktNummer, setAktNummer] = useState<string | undefined>('');
  const formContextRef =
    useRef<IBusinessDataFormContext<IBisattningNode> | null>(null);

  const findOptionByBenamning = (benamning: string) => {
    return props.bisattningSettings?.db?.BOBisattningSettings_All?.find(
      option => option.BENAMNING === benamning
    );
  };

  const typAvUrna = findOptionByBenamning('Typ av Urna');
  const typAvUrnaValidOption = typAvUrna?.VALID_VALUES?.split(' ; ').map(
    (item, index) => ({
      name: item,
      value: item,
    })
  );
  const typAvKista = findOptionByBenamning('Typ av Kista');
  const typAvKistaValidOption = typAvKista?.VALID_VALUES?.split(' ; ').map(
    item => ({
      name: item,
      value: item,
    })
  );
  const vikt = findOptionByBenamning('Vikt');
  const aktnr = findOptionByBenamning('Aktnummer');
  const getHandelseTyp = (aktivBisattningstyp: string) => {
    switch (aktivBisattningstyp) {
      case 'Tom Urna':
        return TEDBOBisattningshandelseTypEnum.BISATTNINGSHANDELSETOM;
      default:
        return TEDBOBisattningshandelseTypEnum.BISATTNINGSHANDELSEIN;
    }
  };

  const getTypeValues = (aktivBisattningstyp: string) => {
    switch (aktivBisattningstyp) {
      case 'Urna / Aska':
        return { TYP_URNA: 'U' };
      case 'Kista':
        return { TYP_KISTA: 'K' };
      case 'Stoft':
        return { TYP_KISTA: 'S' };
      case 'Tom Urna':
        return { TYP_URNA: 'T' };
      default:
        return {};
    }
  };

  //TODO: Flytta ut ur komponenten för att undvika onödiga renderingar
  const validateBisattning: IValidateFn<IBisattningNode> = ({
    currentValues,
    values,
  }) => {
    const validations: IFieldValidations = [];
    const avliden = currentValues._Associations?.Avlidna;
    const avlidenOP =
      currentValues._Associations?.Avlidna?._Associations?.Person;
    const begrbyra = currentValues._Associations?.Begravningsbyra;
    const anh =
      values._Associations?.Kontakter_Add_Values?.[0]?._Associations
        ?.OrgPers_Values;
    const anhAdr =
      values._Associations?.Kontakter_Add_Values?.[0]?._Associations
        ?.OrgPers_Values?._Associations?.Adress_Values;
    const begrDatumTid = avliden?.BEGR_DATUM_TID;
    const gravsDatumTid = avliden?.GRAVS_DATUM_TID;
    const avlidenDatum = avliden?.AVLIDEN_DATUM;
    const pacemaker = values?.PACEMAKER;

    const persNrError = checkPersNr(avlidenOP?.PERS_NR);
    if (persNrError) {
      validations.push(persNrError);
    }

    if (!avlidenOP?.FORNAMN || avlidenOP.FORNAMN === '')
      validations.push({
        fieldName: 'Avlidna.Person.FORNAMN',
        type: 'RequiredValueHint',
      });
    if (!avlidenOP?.EFTERNAMN || avlidenOP.EFTERNAMN === '')
      validations.push({
        fieldName: 'Avlidna.Person.EFTERNAMN',
        type: 'RequiredValueHint',
      });
    if (!avliden?.KREMERING && props.aktivBisattningstyp === 'Kista')
      validations.push({
        fieldName: 'Avlidna.KREMERING',
        type: 'RequiredValueHint',
      });
    if (
      (!begrbyra || begrbyra.ORG_NAMN === '') &&
      props.aktivBisattningstyp !== 'Tom Urna' &&
      !me_data?.me?.[0]?.userIsBegrByra
    )
      validations.push({
        fieldName: 'Begravningsbyra.ORG_NAMN',
        type: 'RequiredValueHint',
      });
    if (!currentValues?.TYP_URNA && props.aktivBisattningstyp === 'Tom Urna')
      validations.push({
        fieldName: 'TYP_URNA',
        type: 'RequiredValueHint',
      });
    if (!currentValues?.TYP_URNA && props.aktivBisattningstyp === 'Tom Urna')
      validations.push({
        fieldName: 'TYP_URNA',
        type: 'RequiredValueHint',
      });

    // Det ska inte vara möjligt att spara om endast adressinformation för den avlidne är ifylld.
    // Minst ett av följande värden (PERS_NR, FORNAMN eller EFTERNAMN) måste också anges
    const hasAvlOrgPersValues =
      !!anh?.PERS_NR || !!anh?.FORNAMN || !!anh?.EFTERNAMN;
    const hasAvlAdressValues =
      !!anhAdr?.CO ||
      !!anhAdr?.ADRESS ||
      !!anhAdr?.POSTNR ||
      !!anhAdr?.ORT ||
      !!anhAdr?.TELEFON1 ||
      !!anhAdr?.SMTP1 ||
      !!values._Associations?.Kontakter_Add_Values?.[0]?.RELATION;
    if (hasAvlAdressValues && !hasAvlOrgPersValues) {
      validations.push({
        fieldName: 'id',
        message: 'Kräver person på avliden',
        type: 'Error',
      });
    }
    if (!!begrDatumTid && begrDatumTid.length < 8) {
      validations.push({
        fieldName: 'id',
        message: 'Kräver datum om tid är angivet',
        type: 'Error',
      });
    }
    if (!!gravsDatumTid && gravsDatumTid.length < 8) {
      validations.push({
        fieldName: 'id',
        message: 'Kräver datum om tid är angivet',
        type: 'Error',
      });
    }
    if (
      !!dodsDatum &&
      !!begrDatumTid &&
      Number(begrDatumTid.substring(0, 8)) < Number(dodsDatum)
    ) {
      validations.push({
        fieldName: 'id',
        message: 'Begravningen kan inte vara tidigare än dödsdatum',
        type: 'Error',
      });
    }
    if (
      !!dodsDatum &&
      !!gravsDatumTid &&
      Number(gravsDatumTid.substring(0, 8)) < Number(dodsDatum)
    ) {
      validations.push({
        fieldName: 'id',
        message: 'Gravsättningen kan inte vara tidigare än dödsdatum',
        type: 'Error',
      });
    }
    if (
      !!begrDatumTid &&
      !!gravsDatumTid &&
      Number(gravsDatumTid.substring(0, 8)) <
        Number(begrDatumTid.substring(0, 8))
    ) {
      validations.push({
        fieldName: 'id',
        message: 'Begravningsdatum måste vara tidigare än Gravsättningsdatum',
        type: 'Error',
      });
    }
    if (
      findOptionByBenamning('Implantat med batteri')?.AKTIV &&
      pacemaker == null &&
      props.aktivBisattningstyp === 'Kista'
    )
      validations.push({
        fieldName: 'PACEMAKER',
        type: 'RequiredValueHint',
      });
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    if (Number(currentDate) < Number(avlidenDatum))
      validations.push({
        fieldName: 'Avlidna.AVLIDEN_DATUM',
        message: 'Dödsdatum får inte vara tidigare än dagens datum',
        type: 'Error',
      });

    return validations;
  };

  const [addMutation, addStatus] = useMutation<
    IAddMutation,
    IAddMutationVariables
  >(AddMutation, {
    refetchQueries: ['collection'],
  });

  const [updateMutation, { loading, error }] = useCollectionMutation<
    IUpdateMutation,
    IUpdateMutationVariables
  >(UpdateMutation);

  const addFunction = useCallback(
    (values: IFormData) => {
      if (!!notering && !!values?.NOTERINGAR) {
        values.NOTERINGAR = notering + '\r\n' + values.NOTERINGAR;
      }
      if (!values._Associations) {
        values._Associations = {};
      }

      if (values._Associations.Avlidna_Values) {
        const avlidnaValues = values._Associations.Avlidna_Values;

        if (props.aktivBisattningstyp === 'Urna / Aska') {
          avlidnaValues.KREMERING = TEDBOBisattningKREMERINGEnum.KREMERING;
        }

        if (avlidnaValues._Associations?.SenasteHandelse_Values) {
          delete avlidnaValues._Associations.SenasteHandelse_Values;
        }

        if (
          avlidnaValues._Associations?.Gravsattningsort_Values &&
          (!avlidnaValues._Associations.Gravsattningsort_Values.id ||
            !avlidnaValues._Associations.Gravsattningsort_Values._version)
        ) {
          delete avlidnaValues._Associations.Gravsattningsort_Values;
        }
      }

      Object.keys(values._Associations).forEach(key => {
        const association = values._Associations![key];
        if (!association || Object.keys(association).length === 0) {
          delete values._Associations![key];
        }
      });

      if (Object.keys(values._Associations).length === 0) {
        delete values._Associations;
      }

      if (
        props.aktivBisattningstyp === 'Urna / Aska' &&
        values?._Associations?.Avlidna_Values
      ) {
        values._Associations.Avlidna_Values.KREMERING =
          TEDBOBisattningKREMERINGEnum.KREMERING;
      }

      const kontakt = values?._Associations?.Kontakter_Add_Values?.[0];
      const kontaktOP = kontakt?._Associations?.OrgPers_Values;

      // Om ny anhörig -> Tillägg av obligatoriska värden
      if (kontakt)
        if (kontaktOP?.FORNAMN || kontaktOP?.EFTERNAMN) {
          kontakt._typ =
            TEDBOBisattningKontaktTypEnum.BISATTNINGSKONTAKTANHORIG;
          if (kontaktOP) {
            kontaktOP._typ = TEDOPOrgPersLokTypEnum.PERSON;
            kontaktOP.LOKALADR_RUBRIK = 'BJKontakt';
            kontaktOP.ANVANDNING_ID = 48;
            if (!kontaktOP._Associations?.Adress_Values)
              kontaktOP._Associations = {
                Adress_Values: {},
              } as TEDInputOPOrgPersLokAddAssociationValues;
          }
        } else if (values?._Associations?.Kontakter_Add_Values) {
          delete values._Associations.Kontakter_Add_Values;
        }
      /** ========== Begravningsbyrå ========== */
      const user = me_data?.me?.[0];

      const begrbyraID_Ref =
        values?._Associations?.Begravningsbyra_Ref ?? begravningsbyraID;
      if (values?._Associations?.Begravningsbyra_Ref)
        delete values._Associations.Begravningsbyra_Ref;

      /**
       * Lägger till den inloggade begravningsbyrån om det inte redan finns en begravningsbyrå
       * associerad med varken BO eller BJ.
       */
      if (
        user?.userIsBegrByra &&
        values?._Associations &&
        !begrbyraID_Ref &&
        !bisattningBegrbyraID
      ) {
        values._Associations.Kontakter_Add_Values ||= [];

        if (user.userOPID) {
          values._Associations.Kontakter_Add_Values.push({
            _typ: TEDBOBisattningKontaktTypEnum.BISATTNINGSKONTAKTBYRA,
            _Associations: {
              OrgPers_Ref: user.userOPID,
            },
          });
        }
      } else if (
        /**
         * Kontrollera att det inte finns en begravningsbyrå i BJ.
         * Kontrollera att det finns en begravningsbyrå i BO.
         * Om båda villkoren är uppfyllda, lägg till samma begravningsbyrå i BJ.
         */
        !bisattningBegrbyraID &&
        begrbyraID_Ref &&
        values?._Associations
      ) {
        if (!values._Associations.Kontakter_Add_Values)
          values._Associations.Kontakter_Add_Values = [];
        values._Associations.Kontakter_Add_Values.push({
          _typ: TEDBOBisattningKontaktTypEnum.BISATTNINGSKONTAKTBYRA,
          _Associations: {
            OrgPers_Ref: begrbyraID_Ref,
          },
        });
      }

      /** ============ Transportör ============ */
      const transportorID_Ref =
        values?._Associations?.Transportor_Ref ?? begravningsbyraID;
      if (values?._Associations?.Transportor_Ref)
        delete values._Associations.Transportor_Ref;
      if (
        user?.userIsBegrByra &&
        values?._Associations &&
        !transportorID_Ref &&
        !bisattningTransportorID
      ) {
        values._Associations.Kontakter_Add_Values ||= [];

        if (user.userOPID) {
          values._Associations.Kontakter_Add_Values.push({
            _typ: TEDBOBisattningKontaktTypEnum.BISATTNINGSKONTAKTTRANSPORTOR,
            _Associations: {
              OrgPers_Ref: user.userOPID,
            },
          });
        }
      } else if (
        /**
         * Kontrollera att det inte finns en transportör i BJ.
         * Kontrollera att det finns en transportör i BO.
         * Om båda villkoren är uppfyllda, lägg till samma transportör i BJ.
         */
        !bisattningTransportorID &&
        transportorID_Ref &&
        values?._Associations
      ) {
        if (!values._Associations.Kontakter_Add_Values)
          values._Associations.Kontakter_Add_Values = [];
        values._Associations.Kontakter_Add_Values.push({
          _typ: TEDBOBisattningKontaktTypEnum.BISATTNINGSKONTAKTTRANSPORTOR,
          _Associations: {
            OrgPers_Ref: transportorID_Ref,
          },
        });
      }

      /** ======== Bisättningshändelse ======== */
      if (values?._Associations) {
        const currentStatus =
          formContextRef.current?.formState?.currentValues?._Associations
            ?.SenasteHandelse?._Associations?.Utcheckingsstatus?.id;
        const currentStatusUrna =
          formContextRef.current?.formState?.currentValues?._Associations
            ?.SenasteHandelse?._Associations?.UtcheckningsstatusUrna?.id;
        const currentStatusStoft =
          formContextRef.current?.formState?.currentValues?._Associations
            ?.SenasteHandelse?._Associations?.UtcheckningsstatusStoft?.id;

        const statusAssociations = (() => {
          if (bisattningsTyp === 'Urna / Aska' && currentStatusUrna) {
            return {
              UtcheckningsstatusUrna_Ref: currentStatusUrna,
            };
          } else if (bisattningsTyp === 'Kista' && currentStatus) {
            return {
              Utcheckingsstatus_Ref: currentStatus,
            };
          } else if (bisattningsTyp === 'Stoft' && currentStatusStoft) {
            return {
              UtcheckningsstatusStoft_Ref: currentStatusStoft,
            };
          } else if (currentStatus) {
            return {
              Utcheckingsstatus_Ref: currentStatus,
            };
          }
          return {};
        })();

        if (!senastBisattningHandelseTyp) {
          let placeringEjMall = null;
          let placeringID = null;
          if (values?._Associations?.SenasteHandelse_Values) {
            placeringEjMall =
              values._Associations.SenasteHandelse_Values.PLACERING_EJ_MALL ||
              null;
            placeringID =
              values._Associations.SenasteHandelse_Values.PLACERING_ID || '';
            delete values?._Associations?.SenasteHandelse_Values;
          }

          const handelseAddValues =
            values._Associations.Handelse_Add_Values || [];

          const newEntry = {
            _typ: getHandelseTyp(props.aktivBisattningstyp),
            DATUM: tryDateTimeToDateTimeString(new Date()),
            TRANSPORTOR: me_data?.me?.[0]?.userIsBegrByra
              ? begravningsbyra_data?.node?.__typename === 'TEDOPOrgPersLok'
                ? begravningsbyra_data?.node?.Namn
                : ''
              : loginStateContext.name,
            _Associations: {
              Anvandare_Ref: me_data?.me?.[0]?.userId,
              Bisattningstyp_Ref: props.bisattningsTypID,
              Lokal_Ref: props.lokalInfo.id,
              Placering_Ref: placeringID,
              ...statusAssociations,
              //TODO lägg till transportör
            },
            PLACERING_EJ_MALL: placeringEjMall,
          };
          handelseAddValues.push({
            ...newEntry,
            _typ: TEDBOBisattningshandelseTypEnum.BISATTNINGSHANDELSEREG,
            PLACERING_EJ_MALL: placeringEjMall,
          });
          handelseAddValues.push(newEntry);

          values._Associations.Handelse_Add_Values = handelseAddValues;
        } else {
          let placeringEjMall = null;
          let placeringID = null;
          let avhamtare = null;
          let tillbaka = null;

          if (values?._Associations?.SenasteHandelse_Values) {
            placeringEjMall =
              values._Associations.SenasteHandelse_Values.PLACERING_EJ_MALL ||
              null;
            placeringID =
              values._Associations.SenasteHandelse_Values.PLACERING_ID || '';
            avhamtare = values._Associations.SenasteHandelse_Values.AVHAMTARE;
            tillbaka = values._Associations.SenasteHandelse_Values.TILLBAKA;
            delete values?._Associations?.SenasteHandelse_Values;
          }
          values._Associations.Handelse_Add_Values = {
            _typ: getHandelseTyp(props.aktivBisattningstyp),
            DATUM: tryDateTimeToDateTimeString(new Date()),
            TRANSPORTOR: me_data?.me?.[0]?.userIsBegrByra
              ? begravningsbyra_data?.node?.__typename === 'TEDOPOrgPersLok'
                ? begravningsbyra_data?.node?.Namn
                : ''
              : loginStateContext.name,
            PLACERING_EJ_MALL: placeringEjMall,
            AVHAMTARE: avhamtare,
            TILLBAKA: tillbaka,
            _Associations: {
              Anvandare_Ref: me_data?.me?.[0]?.userId,
              Bisattningstyp_Ref: bisattningsTypID,
              Lokal_Ref: props.lokalInfo.id,
              Placering_Ref: placeringID,
              ...(() => {
                if (bisattningsTyp === 'Urna / Aska') {
                  return {
                    UtcheckningsstatusUrna_Ref:
                      values._Associations?.SenasteHandelse?._Associations
                        ?.UtcheckningsstatusUrna?.id,
                  };
                } else if (bisattningsTyp === 'Kista') {
                  return {
                    Utcheckingsstatus_Ref:
                      values._Associations?.SenasteHandelse?._Associations
                        ?.Utcheckingsstatus?.id,
                  };
                } else if (bisattningsTyp === 'Stoft') {
                  return {
                    UtcheckningsstatusStoft_Ref:
                      values._Associations?.SenasteHandelse?._Associations
                        ?.UtcheckningsstatusStoft?.id,
                  };
                } else {
                  return {
                    Utcheckingsstatus_Ref:
                      values._Associations?.SenasteHandelse?._Associations
                        ?.Utcheckingsstatus?.id,
                  };
                }
              })(),
              ...statusAssociations,
            },
          };
        }
      }
      /** ============== Avliden ============== */
      if (bisattningID) {
        const avliden = values?._Associations?.Avlidna_Values;
        const avlidenOP = avliden?._Associations?.Person_Values;
        const avlidenOPAdress =
          values?._Associations?.Avlidna_Values?._Associations?.Person_Values
            ?._Associations?.Adress_Values;
        // Referens till AVLIDEN
        if (avlidenID && avlidenVersion && avliden) {
          avliden.id = avlidenID;
          avliden._version = avlidenVersion;
          // Referens till AVLIDEN_PERSON
          if (avlidenPersonID && avlidenPersonVersion && avlidenOP) {
            delete avlidenOP._typ;
            avlidenOP.id = avlidenPersonID;
            avlidenOP._version = avlidenPersonVersion;
            // Referens till AVLIDEN_PERSON_ADRESS
            if (
              avlidenPersonAdressID &&
              avlidenPersonAdressVersion &&
              avlidenOPAdress
            ) {
              avlidenOPAdress.id = avlidenPersonAdressID;
              avlidenOPAdress._version = avlidenPersonVersion;
            }
          }
        }
        return updateMutation({
          variables: {
            db: activeDatabase || '',
            values: {
              id: bisattningID,
              _version: bisattningVersion,
              ...getTypeValues(props.aktivBisattningstyp),
              ...values,
            } as TEDInputBOBisattningUpdateValues,
          },
          refetchQueries: ['rtnWebBisattningsjournal'],
        });
      } else {
        if (values?._Associations?.Avlidna_Values) {
          if (avlidenID) {
            delete values._Associations.Avlidna_Values;
            values._Associations.Avlidna_Ref = avlidenID;
          }
        }
        return addMutation({
          variables: {
            db: activeDatabase || '',
            values: {
              ...getTypeValues(props.aktivBisattningstyp),
              ...values,
            } as TEDInputBOBisattningAddValues,
          },
          refetchQueries: ['rtnWebBisattningsjournal'],
        });
      }
    },
    // TODO: vilka behöver köras ↓↓↓↓
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeDatabase,
      addMutation,
      avlidenID,
      avlidenPersonAdressID,
      avlidenPersonAdressVersion,
      avlidenPersonID,
      avlidenPersonVersion,
      avlidenVersion,
      begravningsbyraID,
      bisattningBegrbyraID,
      bisattningID,
      bisattningVersion,
      props.bisattningsTypID,
      senastBisattningHandelseTyp,
      updateMutation,
      formContextRef,
    ]
  );

  const updateFunction = useCallback(
    (values: IFormData) => {
      const newValues = removeIdsFromNewAssociations(values ?? {});
      return updateMutation({
        variables: {
          db: activeDatabase || '',
          values: {
            id: nodeData?.id ?? '',
            _version: nodeData?._version ?? 1,
            ...newValues,
          },
        },
      });
    },
    [updateMutation, activeDatabase, nodeData?.id, nodeData?._version]
  );

  const activeDatabaseOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabase ?? 'noDBYet',
      },
      skip: !activeDatabase,
    }),
    [activeDatabase]
  );
  const { data: me_data } = useQuery<MeQuery, MeQueryVariables>(
    meQuery,
    activeDatabaseOptions
  );
  const { data: begravningsbyra_data } = useQuery<
    BegravningsbyraQuery,
    BegravningsbyraQueryVariables
  >(begravningsbyraQuery, {
    variables: { id: me_data?.me?.[0]?.userOPID ?? 'NoIDYet' },
    skip: !me_data?.me?.[0]?.userOPID,
  });

  function addQRValues(
    str: string,
    formContext: IBusinessDataFormContext<any, unknown>
  ) {
    const values = str.split('#');
    if (props.aktivBisattningstyp !== 'Tom Urna') {
      formContext?.update([
        {
          operation: 'OwnFields',
          values: {
            TYP_KISTA:
              props.aktivBisattningstyp === 'Kista' ? values[11] : undefined,
          },
        },
        {
          operation: 'Values',
          associationName: 'Avlidna',
          values: {
            KREMERING: values[6]?.toUpperCase(),
            BEGRAVNINGSPLATS: values[4],
            BEGR_DATUM_TID: values[5],
            GRAVSPLATS: values[9],
            GRAVS_DATUM_TID: values[10],
            AKT_MED_URNA: values[7],
          },
        },
        {
          operation: 'Values',
          associationName: 'Person',
          values: {
            _typ: TEDOPOrgPersLokTypEnum.PERSON,
            PERS_NR: values[0],
            FORNAMN: values[2],
            EFTERNAMN: values[1],
            _Associations: { Adress_Values: {} },
          },
        },
      ]);
      setFetchedBegrDatTid(values[5]);
      setFetchedGravsDatTid(values[10]);
    } else
      formContext?.update([
        {
          operation: 'Values',
          associationName: 'Avlidna',
          values: {},
        },
        {
          operation: 'Values',
          associationName: 'Person',
          values: {
            _typ: TEDOPOrgPersLokTypEnum.PERSON,
            PERS_NR: values[0],
            FORNAMN: values[2],
            EFTERNAMN: values[1],
            _Associations: { Adress_Values: {} },
          },
        },
      ]);
    setPersNr(
      values[0].length === 12
        ? values[0].slice(0, 8) + '-' + values[0].slice(8)
        : values[0]
    );
  }

  function clearStates() {
    setAvlidenID('');
    setAvlidenVersion(undefined);
    setAvlidenPersonID('');
    setAvlidenPersonVersion(undefined);
    setAvlidenPersonAdressID('');
    setAvlidenPersonAdressVersion(undefined);
    setPersNr('');
    setBegravningsbyraID('');
    setBisattningBegravningsbyraID('');
    setBisattningTransportorID('');
    setSenastBisattningHandelseTyp('');
    setFetchedBegrDatTid('');
    setFetchedGravsDatTid('');
    setQRText('');
    setAddedBegrDat('');
    setAddedBegrTid('');
    setAddedGravsDat('');
    setAddedGravsTid('');
    setDodsDatum(undefined);
    setBisattningsTyp('');
  }

  const begravningsbyraNamn =
    begravningsbyra_data?.node?.__typename === 'TEDOPOrgPersLok'
      ? begravningsbyra_data?.node?.Namn
      : '';

  const transportorNamn = me_data?.me?.[0]?.userIsBegrByra
    ? begravningsbyra_data?.node?.__typename === 'TEDOPOrgPersLok'
      ? begravningsbyra_data?.node?.Namn
      : ''
    : loginStateContext.name;

  const initialValue = useMemo(
    () => ({
      _Associations: {
        Begravningsbyra_Ref: me_data?.me?.[0]?.userIsBegrByra
          ? {
              id: me_data?.me?.[0]?.userOPID,
              Namn: begravningsbyraNamn,
              KATEGORI: 'BEGRAVNINGSBYRA',
              LOKALADR: 'EXTERN',
            }
          : undefined,
        Transportor_Ref: {
          id: me_data?.me?.[0]?.userOPID,
          Namn: transportorNamn,
          KATEGORI: 'BEGRAVNINGSBYRA',
          LOKALADR: 'EXTERN',
        },
        SenasteHandelse_Values: {
          AVHAMTARE: transportorNamn,
        },
      },
    }),
    [begravningsbyraNamn, me_data?.me, transportorNamn]
  );

  const saveButton: IEditMenuAction = {
    label: 'Spara',
    id: 'Knapp_Spara',
    onClick: ({ businessDataFormContext }) => {
      businessDataFormContext?.saveForm(undefined, () => {
        setIsOpenPDF(true);
      });
    },
    isDisabled: ({ businessDataFormContext }) => {
      let disabledReason = null;

      if ((businessDataFormContext?.formState?.validation?.length ?? 0) > 0) {
        disabledReason =
          businessDataFormContext?.formState?.validation
            ?.filter((item: { message?: any }) => item.message)
            .map(item => item.message).length === 0
            ? null
            : businessDataFormContext?.formState?.validation
                .filter(item => item.message)
                .map(item => <div key={item.message}>{item.message}</div>);
      }

      if (disabledReason) {
        return disabledReason;
      }

      return (businessDataFormContext?.formState?.validation?.length ?? 0) > 0;
    },
    flat: true,
    icon: defaultEditMenuSaveButton.icon,
    hideIconIfNotFloating: defaultEditMenuSaveButton.hideIconIfNotFloating,
  };

  const cancelButton: IEditMenuAction = {
    label: 'Avbryt',
    id: 'Knapp_Avbryt',
    onClick: ({ businessDataFormContext }) => {
      businessDataFormContext?.formStateDispatch({ type: 'CANCEL' });
      businessDataFormContext?.formStateDispatch({
        type: 'ENTER_EDITMODE',
      });
      props.setOpen(false);
      clearStates();
      setBisattningID(undefined);
    },
    flat: true,
    icon: defaultEditMenuCancelButton.icon,
    hideIconIfNotFloating: defaultEditMenuCancelButton.hideIconIfNotFloating,
  };

  const clearButton: IEditMenuAction = {
    label: 'Rensa',
    id: 'Knapp_Rensa',
    onClick: ({ businessDataFormContext }) => {
      businessDataFormContext?.formStateDispatch({ type: 'RESET' });
      businessDataFormContext?.formStateDispatch({
        type: 'ENTER_EDITMODE',
      });
      businessDataFormContext?.formStateDispatch({
        type: 'LOAD_INITIAL_VALUE',
        initialValue: initialValue,
      });

      // Make sure to clear all form-related states
      clearStates();
      setBisattningID(undefined);

      // Reset form associations if needed
      businessDataFormContext?.update([
        {
          operation: 'Values',
          associationName: 'Avlidna',
          values: {},
        },
        {
          operation: 'Values',
          associationName: 'SenasteHandelse',
          values: {},
        },
      ]);
    },
    isDisabled: ({ businessDataFormContext }) => {
      let disabledReason = null;
      if (!businessDataFormContext?.formState?.formHasChanged && !QRText) {
        disabledReason = 'Rensa';
      }
      if (disabledReason) {
        return disabledReason;
      }
      return false;
    },
    flat: true,
    icon: 'clear',
    hideIconIfNotFloating: true,
  };

  return me_data?.me?.[0]?.userOPID ? (
    //Kolla även om du loggar in som begravningsbyra måste isf vänta till man läst in värde
    <BusinessDataForm<IBisattningNode>
      isNew={true}
      newFormContext
      data={nodeData}
      model={bisattningModel}
      updateMutation={updateFunction}
      addMutation={addFunction}
      refetchData={nodeRefetch}
      dataLoading={nodeLoading}
      isEditing
      mutationError={error || addStatus.error}
      mutationLoading={loading || addStatus.loading}
      validateFns={[validateBisattning as IValidateFn]}
      initialValue={initialValue}
      editMenuActions={[cancelButton, clearButton, saveButton]}
    >
      <BusinessDataFormContext.Consumer>
        {formContext => {
          formContextRef.current = formContext;
          {
            formContext?.mutationLoading && <EDAvenyProgress />;
          }
          return (
            <ObjektVy nodeLoading={nodeLoading}>
              <ObjektVySida
                caption={props.aktivBisattningstyp}
                sidNr={1}
                collectionName={bisattningModel.CollectionName}
                noPaddingMargin
              >
                <ObjektVyKort
                  caption={
                    props.aktivBisattningstyp !== 'Tom Urna'
                      ? 'Uppgifter om begravningsbyrå och transportör'
                      : 'Uppgifter om begravningsbyrå'
                  }
                >
                  <EDBox sx={{ display: 'flex' }}>
                    <EDBox marginRight={0.5} width="100%">
                      <ObjektLank<IBisattningAssociations>
                        hintMessage={
                          props.aktivBisattningstyp === 'Tom Urna'
                            ? ''
                            : me_data?.me?.[0]?.userIsBegrByra ||
                              formContext?.formState?.currentValues
                                ?._Associations?.Begravningsbyra
                            ? ''
                            : 'Värde krävs'
                        }
                        required={props.aktivBisattningstyp !== 'Tom Urna'}
                        errorMessage=""
                        associationName="Begravningsbyra"
                        fullWidth
                        disabled={
                          !!begravningsbyraID ||
                          (me_data?.me?.[0]?.userIsBegrByra ?? false)
                        }
                        validValues={{
                          dataDependencies: ['KATEGORI', 'LOKALADR'],
                          validValuesFunction: defaultValues => {
                            return defaultValues.filter(
                              value =>
                                value.data.KATEGORI === 'BEGRAVNINGSBYRA' &&
                                (value.data.LOKALADR === 'EXTERN' ||
                                  value.data.LOKALADR === null)
                            );
                          },
                        }}
                      />
                    </EDBox>
                    {props.aktivBisattningstyp !== 'Tom Urna' && (
                      <EDBox marginLeft={0.5} width="100%">
                        <ObjektLank<IBisattningAssociations>
                          associationName="Transportor"
                          fullWidth
                          disabled={me_data?.me?.[0]?.userIsBegrByra ?? false}
                          validValues={{
                            dataDependencies: ['KATEGORI', 'LOKALADR'],
                            validValuesFunction: defaultValues => {
                              const filteredValues = defaultValues.filter(
                                value =>
                                  value.data.KATEGORI === 'BEGRAVNINGSBYRA' &&
                                  (value.data.LOKALADR === 'EXTERN' ||
                                    value.data.LOKALADR === null)
                              );
                              if (
                                !me_data?.me?.[0]?.userIsBegrByra &&
                                formContext?.formState?.currentValues
                                  ?._Associations?.Transportor?.Namn !==
                                  loginStateContext.name
                              ) {
                                filteredValues.push({
                                  name: loginStateContext.name,
                                  value: me_data?.me?.[0]?.userId ?? '',
                                });
                              }
                              return filteredValues;
                            },
                          }}
                          marker={true}
                        />
                      </EDBox>
                    )}
                  </EDBox>
                </ObjektVyKort>
                <ObjektVyKort caption="LÄS IN QR-KOD">
                  <QrCodeHandler
                    QRText={QRText}
                    setQRText={setQRText}
                    addQRValues={addQRValues}
                    formContext={formContext}
                  />
                </ObjektVyKort>
                <EDBox
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                  }}
                >
                  <EDBox
                    sx={{
                      width:
                        props.aktivBisattningstyp !== 'Tom Urna' &&
                        !isSmallScreen
                          ? '33%'
                          : '100%',
                      marginBottom: isSmallScreen ? 1 : undefined,
                    }}
                  >
                    <ObjektVyKort caption="Avliden">
                      <EDBox sx={{ display: 'flex', gap: 2 }}>
                        <EDBox sx={{ flex: 1 }}>
                          <EDOPAvlidenSearchButtons
                            formContext={formContext}
                            aktivBisattningstyp={props.aktivBisattningstyp}
                            persNr={persNr}
                            setPersNr={setPersNr}
                            fetchPacemaker={
                              findOptionByBenamning('Implantat med batteri')
                                ?.AKTIV ?? false
                            }
                            onCompleteLamnaIn={(
                              avlID,
                              avlVer,
                              begrDatTid,
                              gravsDatTid,
                              avlPersID,
                              avlPersVer,
                              avlDodsDat,
                              avlPersAdrID,
                              avlPersAdrVer,
                              bisID,
                              bisVer,
                              begrbyraID,
                              bisBegrbyraID,
                              bisTranspID,
                              senBisTyp,
                              note,
                              bisattningsTypID,
                              bisattningsTyp,
                              aktNummer
                            ) => {
                              setAvlidenID(avlID);
                              setAvlidenVersion(avlVer);
                              setFetchedBegrDatTid(begrDatTid);
                              setFetchedGravsDatTid(gravsDatTid);
                              setAvlidenPersonID(avlPersID);
                              setAvlidenPersonVersion(avlPersVer);
                              setDodsDatum(avlDodsDat);
                              setAvlidenPersonAdressID(avlPersAdrID);
                              setAvlidenPersonAdressVersion(avlPersAdrVer);
                              setBisattningID(bisID);
                              setBisattningVersion(bisVer);
                              setBegravningsbyraID(begrbyraID);
                              setBisattningBegravningsbyraID(bisBegrbyraID);
                              setBisattningTransportorID(bisTranspID);
                              setSenastBisattningHandelseTyp(senBisTyp);
                              setNotering(note);
                              setBisattningsTypID(bisattningsTypID);
                              setBisattningsTyp(bisattningsTyp);
                              setAktNummer(aktNummer?.toString() ?? undefined);
                            }}
                          />
                        </EDBox>
                        {aktnr && aktnr.AKTIV && persNr !== '' && (
                          <EDBox sx={{ width: '30%' }}>
                            <EDTextEdit
                              label="Aktnummer"
                              value={aktNummer ?? ''}
                              disabled={true}
                              fullWidth
                            />
                          </EDBox>
                        )}
                      </EDBox>
                      <Attribut
                        name="Avlidna.Person.FORNAMN"
                        mode="edit"
                        disabled={Boolean(avlidenID)}
                        isRequired
                      />
                      <Attribut
                        name="Avlidna.Person.EFTERNAMN"
                        mode="edit"
                        disabled={Boolean(avlidenID)}
                        isRequired
                      />
                      <Attribut
                        name="Avlidna.AVLIDEN_DATUM"
                        mode="edit"
                        disabled={Boolean(dodsDatum)}
                        isRequired
                      />
                      {props.aktivBisattningstyp !== 'Tom Urna' && (
                        <>
                          <EDBox sx={{ display: 'flex', alignItems: 'center' }}>
                            <Attribut
                              name="SenasteHandelse.PLACERING_EJ_MALL"
                              customDisplayLabel="Placering"
                              mode="edit"
                              disabled
                            />

                            <EDIconButton
                              marginTop={2}
                              marginLeft={1}
                              icon={
                                <img src={placeringLogo} alt="Placering Logo" />
                              }
                              tooltip="Välj placering"
                              color="inherit"
                              iconSize="medium"
                              onClick={() => {
                                setOpenPlacering(true);
                              }}
                            />
                          </EDBox>

                          {aktnr && aktnr.AKTIV && (
                            <Attribut name="AKTNUMMER" mode="edit" />
                          )}
                          {props.aktivBisattningstyp !== 'Urna / Aska' && (
                            <EDBox
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <Attribut
                                name="Avlidna.KREMERING"
                                mode="edit"
                                fullWidth
                                isRequired
                              />
                              <EDBox marginLeft={1}>
                                <Attribut
                                  name="Avlidna.DIREKTKREM"
                                  mode="edit"
                                  fullWidth
                                  disableChangeMarker
                                  disabled={
                                    formContext?.formState?.currentValues
                                      ?._Associations?.Avlidna?.KREMERING !==
                                    TEDBOBisattningKREMERINGEnum.KREMERING
                                  }
                                />
                              </EDBox>
                            </EDBox>
                          )}
                          {typAvKista &&
                            typAvKista.AKTIV &&
                            props.aktivBisattningstyp !== 'Urna / Aska' && (
                              <EDBox
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Attribut
                                  name="TYP_KISTA"
                                  options={typAvKistaValidOption}
                                  fullWidth
                                />
                                <EDBox marginLeft={1}>
                                  <Attribut
                                    name="EXTRA_STOR_KISTA"
                                    mode="edit"
                                    fullWidth
                                    disableChangeMarker
                                  />
                                </EDBox>
                              </EDBox>
                            )}
                          {props.aktivBisattningstyp !== 'Urna / Aska' && (
                            <Attribut
                              name="KISTMATT"
                              disabled={
                                !formContext?.formState?.values
                                  ?.EXTRA_STOR_KISTA
                              }
                            />
                          )}
                        </>
                      )}
                      {typAvUrna && typAvUrna.AKTIV && (
                        <Attribut
                          name="TYP_URNA"
                          options={typAvUrnaValidOption}
                          fullWidth
                        />
                      )}
                      {props.aktivBisattningstyp !== 'Tom Urna' &&
                        props.aktivBisattningstyp !== 'Urna / Aska' && (
                          <>
                            {vikt && vikt.AKTIV && (
                              <Attribut<IBisattningNode>
                                name="VIKT"
                                mode="edit"
                                fullWidth
                              />
                            )}
                            {findOptionByBenamning('Implantat med batteri')
                              ?.AKTIV && (
                              <Attribut
                                name="PACEMAKER"
                                mode="edit"
                                fullWidth
                              />
                            )}
                          </>
                        )}
                    </ObjektVyKort>
                  </EDBox>
                  {props.aktivBisattningstyp !== 'Tom Urna' && (
                    <EDBox
                      sx={{
                        width: isSmallScreen ? '100%' : '33%',
                        marginBottom: isSmallScreen ? 1 : undefined,
                      }}
                    >
                      <ObjektVyKort caption="Begravning">
                        <>
                          <EDTypography bold>Ceremoni</EDTypography>
                          <Attribut
                            name={'Avlidna.AKT_MED_URNA'}
                            mode={'edit'}
                            disableChangeMarker
                          />
                          <EDDateAndTime
                            formContext={formContext}
                            avlID={avlidenID}
                            attributeName={'BEGR_DATUM_TID'}
                            disabled={!!fetchedBegrDatTid}
                            value={fetchedBegrDatTid}
                            date={addedBegrDat}
                            setDate={setAddedBegrDat}
                            time={addedBegrTid}
                            setTime={setAddedBegrTid}
                          />
                          <Attribut
                            name={'Avlidna.BEGRAVNINGSPLATS'}
                            mode={'edit'}
                            customDisplayLabel="Plats"
                            disabled={!!fetchedBegrDatTid}
                          />
                        </>
                        <EDTypography bold marginTop={2}>
                          Gravsättning
                        </EDTypography>
                        <EDDateAndTime
                          formContext={formContext}
                          avlID={avlidenID}
                          attributeName={'GRAVS_DATUM_TID'}
                          disabled={!!fetchedGravsDatTid}
                          value={fetchedGravsDatTid}
                          date={addedGravsDat}
                          setDate={setAddedGravsDat}
                          time={addedGravsTid}
                          setTime={setAddedGravsTid}
                          dodsDatum={dodsDatum}
                        />
                        <Attribut
                          name={'Avlidna.GRAVSPLATS'}
                          mode={'edit'}
                          customDisplayLabel="Plats"
                          disabled={!!fetchedGravsDatTid}
                        />

                        <Attribut
                          customDisplayLabel="Noteringar"
                          name={'NOTERINGAR'}
                          mode={'edit'}
                          multiline
                          minRows={5}
                          maxRows={5}
                          fullWidth
                        />
                      </ObjektVyKort>
                    </EDBox>
                  )}
                  {props.aktivBisattningstyp !== 'Tom Urna' && (
                    <EDBox sx={{ width: isSmallScreen ? '100%' : '33%' }}>
                      <ObjektVyKort caption="Anhörig">
                        <BusinessDataForm
                          isNew={true}
                          association={'Kontakter'}
                          associatedId={'NewAnhorig'}
                          model={bisattningKontakterModel}
                        >
                          <Attribut name={'OrgPers.PERS_NR'} mode={'edit'} />
                          <Attribut name={'OrgPers.FORNAMN'} mode={'edit'} />
                          <Attribut name={'OrgPers.EFTERNAMN'} mode={'edit'} />
                          <Attribut name={'OrgPers.Adress.CO'} mode={'edit'} />
                          <Attribut
                            name={'OrgPers.Adress.ADRESS'}
                            mode={'edit'}
                          />
                          <EDBox sx={{ display: 'flex' }}>
                            <EDBox marginRight={0.5} width="100%">
                              <Attribut
                                name={'OrgPers.Adress.POSTNR'}
                                mode={'edit'}
                              />
                            </EDBox>
                            <EDBox marginLeft={0.5} width="100%">
                              <Attribut
                                name={'OrgPers.Adress.ORT'}
                                mode={'edit'}
                              />
                            </EDBox>
                          </EDBox>
                          <Attribut
                            name={'OrgPers.Adress.TELEFON1'}
                            mode={'edit'}
                          />
                          <Attribut
                            name={'OrgPers.Adress.SMTP1'}
                            mode={'edit'}
                          />
                          <Attribut name={'RELATION'} mode={'edit'} />
                        </BusinessDataForm>
                      </ObjektVyKort>
                    </EDBox>
                  )}
                </EDBox>

                {isOpenPDF && (
                  <Etikett
                    bjid={bisattningID ?? formContext?.formState?.addedId ?? ''}
                    onClose={() => {
                      props.setOpen(false);
                      setIsOpenPDF(false);
                    }}
                  />
                )}
                <EDDialog
                  open={
                    props.aktivBisattningstyp !== 'Tom Urna'
                      ? senastBisattningHandelseTyp ===
                        TEDBOBisattningshandelseTypEnum.BISATTNINGSHANDELSEIN
                      : senastBisattningHandelseTyp ===
                        TEDBOBisattningshandelseTypEnum.BISATTNINGSHANDELSETOM
                  }
                  onClose={() => {
                    formContext?.formStateDispatch({ type: 'RESET' });
                    formContext?.formStateDispatch({
                      type: 'ENTER_EDITMODE',
                    });
                    formContext?.formStateDispatch({
                      type: 'LOAD_INITIAL_VALUE',
                      initialValue: initialValue,
                    });
                    clearStates();
                    setBisattningID(undefined);
                  }}
                >
                  <EDTypography testId="BisattningInfoMessage">
                    {senastBisattningHandelseTyp ===
                    TEDBOBisattningshandelseTypEnum.BISATTNINGSHANDELSEIN
                      ? 'Den avlidna är redan inlämnad'
                      : 'En tom urna har redan lämnats in'}
                  </EDTypography>
                </EDDialog>
                <ValjPlacering
                  formContext={formContext}
                  open={isOpenPlacering}
                  onClose={() => setOpenPlacering(false)}
                  lokalID={props.lokalInfo.index ?? -1}
                  typ={
                    props.aktivBisattningstyp === 'Kista'
                      ? 'K'
                      : props.aktivBisattningstyp === 'Urna / Aska'
                      ? 'U'
                      : props.aktivBisattningstyp === 'Stoft'
                      ? 'S'
                      : '-'
                  }
                />
              </ObjektVySida>
            </ObjektVy>
          );
        }}
      </BusinessDataFormContext.Consumer>
    </BusinessDataForm>
  ) : null;
};

export default LamnaIn;
